import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { EVENT_DATE, EVENT_END_DATE, RSVP_DEADLINE } from '../constants';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const authHeaders = () => {
  const token = sessionStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const DEFAULT_SCHEDULE = [
  { id: '1', time: '2:00 PM',  description: 'Arrive - get settled... thunderstruck??' },
  { id: '2', time: '2:30 PM',  description: 'Team draw - who will you dominate with?' },
  { id: '3', time: '3:00 PM',  description: 'Let the Games begin!' },
  { id: '4', time: '6:00 PM',  description: "Dinner - you've earned it!" },
  { id: '5', time: 'Evening',  description: "Stay as long as you'd like - the night is yours" },
];

const DEFAULT_HIGHLIGHTS = [
  'Team USA vs Team Canada competition',
  'Food, drinks, and great company',
  'Prizes for the winning team',
  'Cheerleader section for supporters',
];

// Parse a stored ISO string into a Date, falling back to the default if missing/invalid
const parseSettingsDate = (iso, fallback) => {
  if (!iso) return fallback;
  const d = new Date(iso);
  return isNaN(d) ? fallback : d;
};

export const AppProvider = ({ children }) => {
  const [invites, setInvites] = useState([]);
  const [rsvps,   setRsvps]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Runtime event settings (overrides constants once loaded from DB)
  const [settings, setSettings] = useState({
    eventDate:        EVENT_DATE,
    eventEndDate:     EVENT_END_DATE,
    rsvpDeadline:     RSVP_DEADLINE,
    enforceDeadline:  true,
    eventDescription: 'Annual CanAm Event — Team USA vs Team Canada!',
    eventLocation:    '137 Northington Dr., East Amherst, NY 14051',
    videos:           [],
    featuredVideoId:  null,
    schedule:         DEFAULT_SCHEDULE,
    highlights:       DEFAULT_HIGHLIGHTS,
    attire:           '',
  });

  // ── initial data load ────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, rsvpRes, settingsRes] = await Promise.all([
        fetch(`${API}/invites`),
        fetch(`${API}/rsvps`),
        fetch(`${API}/settings`),
      ]);
      if (!invRes.ok || !rsvpRes.ok) throw new Error('Server error');
      const [invData, rsvpData] = await Promise.all([invRes.json(), rsvpRes.json()]);
      setInvites(invData);
      setRsvps(rsvpData);
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        setSettings({
          eventDate:        parseSettingsDate(s.event_date,     EVENT_DATE),
          eventEndDate:     parseSettingsDate(s.event_end_date, EVENT_END_DATE),
          rsvpDeadline:     parseSettingsDate(s.rsvp_deadline,  RSVP_DEADLINE),
          enforceDeadline:  s.enforce_deadline !== 'false' && s.enforce_deadline !== '0',
          eventDescription: s.event_description || 'Annual CanAm Event — Team USA vs Team Canada!',
          eventLocation:    s.event_location    || '137 Northington Dr., East Amherst, NY 14051',
          videos:           s.videos ? JSON.parse(s.videos) : [],
          featuredVideoId:  s.featured_video_id || null,
          schedule:         s.schedule    ? JSON.parse(s.schedule)    : DEFAULT_SCHEDULE,
          highlights:       s.highlights  ? JSON.parse(s.highlights)  : DEFAULT_HIGHLIGHTS,
          attire:           s.attire || '',
        });
      }
    } catch {
      setError('Could not connect to the server. Make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Silent background refresh every 30 seconds — keeps all devices in sync
  useEffect(() => {
    const poll = setInterval(() => {
      const token = sessionStorage.getItem('admin_token');
      const inviteUrl = token ? `${API}/invites/full` : `${API}/invites`;
      const inviteHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      Promise.all([
        fetch(inviteUrl, { headers: inviteHeaders }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/rsvps`).then(r => r.ok ? r.json() : null),
      ]).then(([inv, rsvp]) => {
        if (inv)  setInvites(inv);
        if (rsvp) setRsvps(rsvp);
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(poll);
  }, []);

  // ── admin: reload full invite data (with PII) after login ─
  const loadFullInvites = useCallback(async () => {
    const res = await fetch(`${API}/invites/full`, { headers: authHeaders() });
    if (!res.ok) return;
    const data = await res.json();
    setInvites(data);
  }, []);

  // ── RSVP ─────────────────────────────────────────────────
  const submitRSVP = async (rsvpData) => {
    const res = await fetch(`${API}/rsvps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rsvpData), // rsvpData may include optional phone
    });
    if (!res.ok) throw new Error('Failed to submit RSVP');
    const saved = await res.json();
    setRsvps(prev => {
      const idx = prev.findIndex(r => r.inviteeId === saved.inviteeId);
      return idx >= 0
        ? prev.map(r => r.inviteeId === saved.inviteeId ? saved : r)
        : [...prev, saved];
    });
    return saved;
  };

  // Admin-only direct edit
  const updateRSVP = async (inviteeId, updates) => {
    const res = await fetch(`${API}/rsvps/${inviteeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error('Failed to update RSVP');
    const saved = await res.json();
    setRsvps(prev => {
      const idx = prev.findIndex(r => r.inviteeId === saved.inviteeId);
      return idx >= 0
        ? prev.map(r => r.inviteeId === saved.inviteeId ? saved : r)
        : [...prev, saved];
    });
    return saved;
  };

  const getRSVPByInviteeId = (inviteeId) => rsvps.find(r => r.inviteeId === inviteeId);

  const getAttendees = () =>
    rsvps
      .filter(r => r.status !== 'unavailable')
      .map(r => {
        const invite = invites.find(i => i.id === r.inviteeId);
        return { ...invite, ...r, name: invite?.name };
      });

  // ── invites ───────────────────────────────────────────────
  const addInvite = async (inviteData) => {
    const res = await fetch(`${API}/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(inviteData),
    });
    if (!res.ok) throw new Error('Failed to add invite');
    const saved = await res.json();
    setInvites(prev => [...prev, saved]);
    return saved;
  };

  const addInvitesBulk = async (invitees) => {
    const res = await fetch(`${API}/invites/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ invitees }),
    });
    if (!res.ok) throw new Error('Failed to bulk import invites');
    const { added } = await res.json();
    setInvites(prev => [...prev, ...added]);
    return added;
  };

  const updateInvite = async (inviteId, updatedData) => {
    const res = await fetch(`${API}/invites/${inviteId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(updatedData),
    });
    if (!res.ok) throw new Error('Failed to update invite');
    const saved = await res.json();
    setInvites(prev => prev.map(i => i.id === inviteId ? saved : i));
    return saved;
  };

  const toggleInviteSent = async (inviteId) => {
    const invite = invites.find(i => i.id === inviteId);
    return updateInvite(inviteId, { inviteSent: !invite.inviteSent });
  };

  // ── settings ──────────────────────────────────────────────
  const updateSettings = async (newSettings) => {
    const res = await fetch(`${API}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(newSettings),
    });
    if (!res.ok) throw new Error('Failed to save settings');
    const s = await res.json();
    setSettings({
      eventDate:        parseSettingsDate(s.event_date,     EVENT_DATE),
      eventEndDate:     parseSettingsDate(s.event_end_date, EVENT_END_DATE),
      rsvpDeadline:     parseSettingsDate(s.rsvp_deadline,  RSVP_DEADLINE),
      enforceDeadline:  s.enforce_deadline !== 'false' && s.enforce_deadline !== '0',
      eventDescription: s.event_description || 'Annual CanAm Event — Team USA vs Team Canada!',
      eventLocation:    s.event_location    || '137 Northington Dr., East Amherst, NY 14051',
      videos:           s.videos ? JSON.parse(s.videos) : [],
      featuredVideoId:  s.featured_video_id || null,
      schedule:         s.schedule    ? JSON.parse(s.schedule)    : DEFAULT_SCHEDULE,
      highlights:       s.highlights  ? JSON.parse(s.highlights)  : DEFAULT_HIGHLIGHTS,
      attire:           s.attire || '',
    });
  };

  const clearGuestList = async () => {
    const res = await fetch(`${API}/settings/clear-guests`, { method: 'POST', headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to clear guest list');
    setInvites([]);
    setRsvps([]);
  };

  const deleteInvite = async (inviteId) => {
    const res = await fetch(`${API}/invites/${inviteId}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to delete invite');
    setInvites(prev => prev.filter(i => i.id !== inviteId));
    setRsvps(prev => prev.filter(r => r.inviteeId !== inviteId));
  };

  const value = {
    invites,
    rsvps,
    loading,
    error,
    loadData,
    loadFullInvites,
    submitRSVP,
    updateRSVP,
    getRSVPByInviteeId,
    getAttendees,
    addInvite,
    addInvitesBulk,
    updateInvite,
    toggleInviteSent,
    deleteInvite,
    settings,
    updateSettings,
    clearGuestList,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
